// Settings: the Sign-in tab (02-DESIGN/03-console/01-the-console.md
// § Settings: the Sign-in tab; decision 0055; issue 182). One card, for the
// one provider the workspace signs in through — Impire sign-in, the
// workspace's own, or the managed one its parent gives it — never two side
// by side. The workspace's own is set up in a form in the tab's place, in
// four steps, that hands over the values to register at the provider, each
// with copy, and saving it changes nothing for anyone; a switch, either way,
// is a form of its own in the tab's place that says who carries over and asks for a sign-in through the provider switched
// to as proof (02-DESIGN/01-identity/03-signing-in.md § switching a
// workspace's provider). That sign-in returns here; its token is handed to
// identity.provider.use, and on success the page keeps it as the session,
// since the switch signed everyone out. A plan without the workspace's own
// sign-in is read beforehand from identity.tenant.get and said in the
// plan's name, with a link to the Plan tab (02-DESIGN/17-plans/
// 02-features-and-limits.md § the console).

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { PLATFORM_SIGN_IN, providerName } from "../../platform/names.ts";
import { allows, Surface } from "../../platform/profile.ts";
import { usePage } from "../../platform/session.tsx";
import { begin, redirectUri } from "../../signin/flow.ts";
import { proofChoice } from "../../signin/provider.ts";
import { takeProof, type Probe, type Proof } from "../../signin/tokens.ts";
import {
  Button,
  Choices,
  describe,
  Field,
  NotAllowed,
  Notice,
  OnceValue,
  Pending,
  plural,
  TextInput,
  useLoad,
} from "../../ui/components.tsx";
import { FormPage, formOf, useFormState, withForm } from "../../ui/form.tsx";
import { RowMenu, type MenuEntry } from "../../ui/table.tsx";
import {
  Code,
  Feature,
  PlanCode,
  PROVIDER_INHERITED,
  ProviderID,
  Subjects,
  type ManagedProviderReply,
  type ProviderConfig,
  type ProviderPutRequest,
  type ProviderReply,
  type ProviderUseReply,
  type ProviderUseRequest,
  type RowPutRequest,
  type RowsListReply,
} from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { LOOPBACK_REDIRECTS } from "../apps/desktop.ts";
import { useSurface } from "../surface.tsx";
import { useManaged, type Managed } from "../workspaces/managed.ts";
import { providerFieldsOf, nowhere } from "./provider.ts";
import { ProviderFields } from "./ProviderFields.tsx";
import { PlanLink, usePlan } from "./PlanNotes.tsx";
import {
  carryOf,
  inUseOf,
  isRule,
  KIND_LABELS,
  kindOf,
  presetOf,
  PRESETS,
  refusalOf,
  clientsOf,
  draftFor,
  keptDraft,
  setUpName,
  setUpOf,
  setUpStepOf,
  withDraft,
  whoOf,
  type SetUpDraft,
  type Step,
} from "./sign-in.ts";

/**
 * SignInForm names the Sign-in tab's forms, each in the tab's place: the
 * set-up, the change of one already set up, and the switch either way. The
 * prefix tells the page which tab a refreshed form stands in.
 */
export type SignInForm =
  | "sign-in/set-up"
  | "sign-in/change"
  | "sign-in/switch"
  | "sign-in/switch-back"
  | `sign-in/set-up:${string}`
  | `sign-in/change:${string}`;

function entitlement(err: unknown): boolean {
  if (typeof err !== "object" || err === null) {
    return false;
  }
  const code = "code" in err && typeof err.code === "string" ? err.code : "";
  const message = "message" in err && typeof err.message === "string" ? err.message : "";
  return (
    code === PlanCode.notInPlan ||
    (code === Code.unauthorized && /plan does not include|entitle/i.test(message))
  );
}

/** shortIssuer is an issuer's address without its scheme, for the quiet line under the card. */
function shortIssuer(iss: string | undefined): string {
  return (iss ?? "").replace(/^https?:\/\//, "");
}

export function SignInTab() {
  const s = useSurface();
  const { setSession } = usePage();
  const [current, reload] = useLoad("provider", () =>
    s.bus.call<ProviderReply>(SERVICE, Subjects.providerGet(), undefined),
  );
  const managed = useManaged();
  const [held] = useLoad(`managed-provider:${String(managed.managed)}`, () =>
    managed.managed
      ? s.bus.call<ManagedProviderReply>(SERVICE, Subjects.managedProviderGet(), undefined)
      : Promise.resolve<ManagedProviderReply>({}),
  );
  const [proof] = useState<Proof | undefined>(() => takeProof());
  const [proving, setProving] = useState<"idle" | "busy" | "done">(proof === undefined ? "idle" : "busy");
  const [proved, setProved] = useState<ProviderUseReply | undefined>(undefined);
  const [proofErr, setProofErr] = useState<unknown>(undefined);
  const form = useFormState<SignInForm>();
  const [note, setNote] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);

  const reply = current.state === "ready" ? current.value : undefined;
  const config = reply?.config;
  const inUse = inUseOf(reply?.in_use);
  const managedReply = held.state === "ready" ? held.value : undefined;
  const managedConfig = managedReply?.config;
  const ownRefused = managedConfig !== undefined && managedReply?.own_provider === "refused";
  const plan = usePlan();
  // The plan read beforehand: a plan that answers its features and withholds this one.
  const withheld =
    plan.tenant?.entitlement !== undefined &&
    plan.tenant.entitlement.features[Feature.ownProvider]?.granted !== true;
  const notInPlan = (current.state === "refused" && entitlement(current.err)) || withheld;
  const ownName = config !== undefined ? providerName(config) : "";
  const inheritedName = managedConfig !== undefined ? `${managed.by}'s sign-in` : PLATFORM_SIGN_IN;

  // A sign-in that proves a switch came back here: hand its token to the
  // switch, then keep it, since everyone was signed out.
  useEffect(() => {
    if (proof === undefined) {
      return;
    }
    const body: ProviderUseRequest = {
      provider: proof.probe === "own" ? ProviderID.own : PROVIDER_INHERITED,
      credential: proof.session.credential,
    };
    s.bus.call<ProviderUseReply>(SERVICE, Subjects.providerUse(), body, 30000).then(
      (r) => {
        setProved(r);
        setProving("done");
        setTimeout(() => {
          setSession(proof.session);
          window.location.assign(`${tenantPath(s.tenant)}/sign-in`);
        }, 2500);
      },
      (e: unknown) => {
        setProofErr(e);
        setProving("idle");
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the proof is handed over once, as the page loaded with it
  }, []);

  const remove = () => {
    setErr(undefined);
    s.bus.call<ProviderReply>(SERVICE, Subjects.providerDelete(), undefined).then(
      () => {
        setNote(`${s.name}'s own sign-in is removed.`);
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };

  const proofName = proof?.probe === "own" ? ownName || "the workspace's own sign-in" : inheritedName;

  // A form stands in the tab's place; one the reply does not admit yet — a
  // refresh before the provider is read — waits for it.
  // A set-up in progress keeps its own steps: a provider saved at its fourth
  // step is read again only once the form closes.
  const setUp = setUpOf(form.open, config !== undefined);
  const switching: Probe | undefined =
    form.open === "sign-in/switch" && config !== undefined
      ? "own"
      : form.open === "sign-in/switch-back"
        ? "inherited"
        : undefined;
  if (current.state === "ready" && setUp !== undefined) {
    return (
      <SetUp
        initial={setUp === "change" ? config : undefined}
        inUse={inUse === "own"}
        onClose={form.close}
        onSaved={(switchNow) => {
          form.close();
          reload();
          if (switchNow) {
            form.show("sign-in/switch");
          } else {
            setNote(`${s.name}'s own sign-in was checked against the provider and saved.`);
          }
        }}
      />
    );
  }
  if (current.state === "ready" && switching !== undefined) {
    return (
      <Switch
        target={switching}
        fromId={
          inUse === "own" ? ProviderID.own : inUse === "managed" ? ProviderID.managed : ProviderID.platform
        }
        fromSubjectClaim={inUse === "own" ? config?.subject_claim : undefined}
        toName={switching === "own" ? ownName : inheritedName}
        own={config}
        managedConfig={managedConfig}
        onClose={form.close}
      />
    );
  }

  return (
    <>
      {proving === "busy" && <Notice kind="info">Switching {s.name}&apos;s sign-in…</Notice>}
      {proving === "done" && proved !== undefined && (
        <Notice kind="ok" title={`${s.name} signs in with ${proofName} now`}>
          Everyone was signed out; you are signed in again with {proofName}.{" "}
          {proved.carried.length > 0 &&
            `${plural(proved.carried.length, "person keeps", "people keep")} their role, waiting for their first sign-in. `}
          {proved.not_carried.length > 0 &&
            `${plural(proved.not_carried.length, "access rule or person")} did not carry over; add them again on People.`}
        </Notice>
      )}
      {proofErr !== undefined && <Notice kind="bad">{refusalOf(proofErr, proofName)}</Notice>}
      {err !== undefined && <NotAllowed err={err} />}
      {note !== undefined && <Notice kind="ok">{note}</Notice>}

      {current.state === "pending" && <Pending what="Reading" />}
      {current.state === "refused" && !notInPlan && <NotAllowed err={current.err} what="Sign-in" />}

      {(current.state === "ready" || notInPlan) && inUse === "own" && config !== undefined && (
        <OwnCard
          name={s.name}
          config={config}
          inheritedName={inheritedName}
          onChange={() => {
            form.show("sign-in/change");
          }}
          onSwitchBack={() => {
            form.show("sign-in/switch-back");
          }}
        />
      )}
      {(current.state === "ready" || notInPlan) && inUse !== "own" && (
        <InheritedCard
          name={s.name}
          managed={managed}
          managedReply={managedReply}
          config={config}
          notInPlan={notInPlan}
          holder={plan.holder}
          ownRefused={ownRefused}
          onSetUp={() => {
            form.show(config === undefined ? "sign-in/set-up" : "sign-in/change");
          }}
          onSwitch={() => {
            form.show("sign-in/switch");
          }}
          onRemove={remove}
        />
      )}
    </>
  );
}

/** InheritedCard is the card while the workspace signs in with what it inherits: Impire sign-in, or its parent's. */
function InheritedCard({
  name,
  managed,
  managedReply,
  config,
  notInPlan,
  holder,
  ownRefused,
  onSetUp,
  onSwitch,
  onRemove,
}: {
  name: string;
  managed: Managed;
  managedReply: ManagedProviderReply | undefined;
  config: ProviderConfig | undefined;
  notInPlan: boolean;
  /** Whose plan covers the workspace, by name. */
  holder: string;
  ownRefused: boolean;
  onSetUp: () => void;
  onSwitch: () => void;
  onRemove: () => void;
}) {
  const managedConfig = managedReply?.config;
  const ownName = config !== undefined ? providerName(config) : "";
  const required = (managedReply?.rows ?? []).filter((r) => r.required === true).length;
  const offerOwn = !notInPlan && !ownRefused;
  const menu: MenuEntry[] =
    config !== undefined && offerOwn
      ? [
          { label: "Change", onSelect: onSetUp },
          {
            label: "Remove",
            onSelect: onRemove,
            danger: true,
            confirm: {
              title: `Remove ${name}'s own sign-in?`,
              consequence: `${ownName} is not in use, so nobody is signed out. Its access rules stay unused.`,
            },
          },
        ]
      : [];
  return (
    <div className="card">
      <div className="section-head" style={{ marginBottom: 8 }}>
        <h2>{managedConfig !== undefined ? `Managed by ${managed.by}` : PLATFORM_SIGN_IN}</h2>
        <div className="row">
          {offerOwn &&
            (config === undefined ? (
              <Button kind="primary" onClick={onSetUp}>
                Set up {name}&apos;s own sign-in
              </Button>
            ) : (
              <Button kind="primary" onClick={onSwitch}>
                Switch to {ownName}
              </Button>
            ))}
          {menu.length > 0 && <RowMenu label={`More for ${name}'s own sign-in`} entries={menu} />}
        </div>
      </div>
      {managedConfig !== undefined ? (
        <p className="muted">
          {managed.by} sets how {name} signs in: with {providerName(managedConfig)}.{" "}
          {required > 0 &&
            `${plural(required, "required rule")}, marked as ${managed.by}'s on People's Access rules.`}
        </p>
      ) : (
        <p className="muted">
          {name} signs in with Impire. People use the account they already have, and the access rules on
          People decide who gets in.
        </p>
      )}
      {config !== undefined && offerOwn && (
        <p className="small muted" style={{ marginTop: 8 }}>
          {name} signs in with {managedConfig !== undefined ? `${managed.by}'s sign-in` : PLATFORM_SIGN_IN}.{" "}
          {ownName} is set up, not in use.
        </p>
      )}
      {config === undefined && offerOwn && (
        <p className="small muted" style={{ marginTop: 8 }}>
          Use your company&apos;s provider — Microsoft Entra, Okta, Google — so people sign in with their work
          account. Setting it up changes nothing until you switch to it.
        </p>
      )}
      {notInPlan && (
        <p className="small muted" style={{ marginTop: 8 }}>
          {name}&apos;s own sign-in is not in {holder}&apos;s plan. <PlanLink />
        </p>
      )}
      {ownRefused && (
        <p className="small muted" style={{ marginTop: 8 }}>
          {managed.by} does not allow a sign-in of {name}&apos;s own.
        </p>
      )}
    </div>
  );
}

/** OwnCard is the card while the workspace signs in with its own provider. */
function OwnCard({
  name,
  config,
  inheritedName,
  onChange,
  onSwitchBack,
}: {
  name: string;
  config: ProviderConfig;
  inheritedName: string;
  onChange: () => void;
  onSwitchBack: () => void;
}) {
  const ownName = providerName(config);
  return (
    <div className="card">
      <div className="section-head" style={{ marginBottom: 8 }}>
        <h2>{ownName}</h2>
        <div className="row">
          <Button kind="primary" onClick={onChange}>
            Change
          </Button>
          <RowMenu
            label={`More for ${ownName}`}
            entries={[{ label: `Switch back to ${inheritedName}`, onSelect: onSwitchBack }]}
          />
        </div>
      </div>
      <p className="muted">
        {name} signs in with {ownName}.
      </p>
      <dl className="kv small" style={{ marginTop: 8 }}>
        <dt>Issuer</dt>
        <dd className="mono">{shortIssuer(config.issuer)}</dd>
        <dt>Desktop AI clients</dt>
        <dd>{allows(config, Surface.desktop) ? "allowed" : "not allowed"}</dd>
      </dl>
      <p className="small faint" style={{ marginTop: 8 }}>
        Switch back to {inheritedName} first to remove it.
      </p>
    </div>
  );
}

/**
 * SetUp is the set-up of the workspace's own sign-in, in four steps — which
 * provider, register the workspace there, copy back what it shows, who gets
 * in — and, changing one already saved, the third alone with its issuer fixed
 * while it is in use. Saving changes nothing for anyone.
 */
function SetUp({
  initial,
  inUse,
  onClose,
  onSaved,
}: {
  initial: ProviderConfig | undefined;
  inUse: boolean;
  onClose: () => void;
  onSaved: (switchNow: boolean) => void;
}) {
  const s = useSurface();
  const { origin } = usePage();
  const changing = initial !== undefined;
  const location = useLocation();
  const navigate = useNavigate();
  // A set-up under way names its provider and step in the history entry and
  // keeps its values there, the client secret never: a refresh returns to
  // the same step with what was typed (the secret typed again).
  const under = setUpStepOf(formOf(location.state));
  const [step, setStep] = useState<Step | 5>(changing ? 3 : (under?.step ?? 1));
  const [d, setD] = useState<SetUpDraft>(() => {
    const base = draftFor(changing ? kindOf(initial) : (under?.kind ?? "entra"), initial);
    return under === undefined ? base : keptDraft(location.state, base);
  });
  const here = location.pathname + location.search;
  const state: unknown = location.state;
  const keptName = step === 5 ? undefined : setUpName(changing, d.kind, step);
  useEffect(() => {
    if (keptName === undefined) {
      return;
    }
    const base = typeof state === "object" && state !== null ? (state as Record<string, unknown>) : {};
    void navigate(here, { replace: true, state: withDraft(withForm(base, keptName), d) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- written when the step or a value changes, not when the entry it wrote does
  }, [keptName, d]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const preset = presetOf(d.kind);
  const providerWord = d.displayName.trim() !== "" ? d.displayName.trim() : "your provider";
  const set = (patch: Partial<SetUpDraft>) => {
    setD((prev) => ({ ...prev, ...patch }));
  };
  const needsSecret = preset.secret || (initial?.exchange ?? "") === "server-side";
  const issuerChanged = changing && inUse && d.issuer.trim() !== (initial.issuer ?? "");
  const ready =
    d.issuer.trim() !== "" && !nowhere(d) && !issuerChanged && (!needsSecret || changing || d.secret !== "");

  const save = () => {
    if (busy || !ready) {
      return;
    }
    setErr(undefined);
    const config: ProviderConfig = {
      id: ProviderID.own,
      lane: "oidc",
      ...providerFieldsOf(d),
      display_name: d.displayName.trim(),
      exchange: needsSecret ? "server-side" : "public",
    };
    const body: ProviderPutRequest = { config };
    if (needsSecret && d.secret !== "") {
      body.client_secret = d.secret;
    }
    setBusy(true);
    s.bus
      .call<ProviderReply>(SERVICE, Subjects.providerPut(), body, 30000)
      .then(async () => {
        if (!changing && d.ruleValue.trim() !== "") {
          const row: RowPutRequest = {
            row: {
              provider: ProviderID.own,
              claim: d.ruleClaim,
              value: d.ruleValue.trim(),
              role: "admin",
              pending: d.ruleClaim === "email",
              written: new Date().toISOString(),
            },
          };
          await s.bus.call(SERVICE, Subjects.rowsPut(), row);
        }
      })
      .then(
        () => {
          setBusy(false);
          if (changing) {
            onSaved(false);
          } else {
            setStep(5);
          }
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };

  const back = () => {
    setStep((n) => (n > 1 ? ((n - 1) as Step) : n));
  };
  const next = () => {
    setStep((n) => (n < 4 ? ((n + 1) as Step) : n));
  };
  const title = changing ? `Change ${s.name}'s own sign-in` : `Set up ${s.name}'s own sign-in`;
  const redirect = redirectUri(origin);

  let foot;
  if (step === 5) {
    foot = (
      <>
        <Button
          kind="quiet"
          onClick={() => {
            onSaved(false);
          }}
        >
          Done
        </Button>
        <Button
          kind="primary"
          onClick={() => {
            onSaved(true);
          }}
        >
          Switch to {providerWord}
        </Button>
      </>
    );
  } else {
    // Every step keeps *Cancel* first; a later step adds *Back*; the saving
    // action — *Next*, or *Save* on the last — is last.
    const saves = changing || step === 4;
    foot = (
      <>
        <Button kind="quiet" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {!changing && step > 1 && (
          <Button kind="quiet" onClick={back} disabled={busy}>
            Back
          </Button>
        )}
        {saves ? (
          <Button kind="primary" onClick={save} disabled={busy || !ready}>
            {busy ? "Checking and saving…" : "Save"}
          </Button>
        ) : (
          <Button kind="primary" onClick={next} disabled={step === 3 && !ready}>
            Next
          </Button>
        )}
      </>
    );
  }

  return (
    <FormPage
      title={title}
      line={step === 5 ? undefined : changing ? undefined : `Step ${String(step)} of 4.`}
      heading="h2"
      onClose={onClose}
      foot={foot}
    >
      {err !== undefined && <NotAllowed err={err} what="Save" />}
      {step === 1 && (
        <div className="stack">
          <p className="muted">Which provider do {s.name}&apos;s people sign in with at work?</p>
          <Choices
            name="provider-kind"
            value={d.kind}
            onChange={(k) => {
              setD(draftFor(k));
            }}
            options={PRESETS.map((p) => ({ value: p.kind, label: KIND_LABELS[p.kind] }))}
          />
        </div>
      )}
      {step === 2 && (
        <div className="stack">
          <h3>
            Register {s.name} at {providerWord}
          </h3>
          <p className="muted">{preset.creates}</p>
          <OnceValue label={preset.redirectField} value={redirect} />
          {preset.logoutField !== undefined && <OnceValue label={preset.logoutField} value={redirect} />}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={d.desktop}
              onChange={(e) => {
                set({ desktop: e.target.checked });
              }}
            />
            Allow desktop AI clients
          </label>
          {d.desktop &&
            LOOPBACK_REDIRECTS.map((r) => (
              <OnceValue
                key={r}
                label={
                  preset.twoClients
                    ? "Loopback redirect, on the terminal's client"
                    : "Loopback redirect, for desktop AI clients"
                }
                value={r}
              />
            ))}
          {d.desktop && (
            <p className="small faint">
              A desktop AI client such as Claude Code returns to it on any port it picks; where the provider
              matches the port exactly, add the port the client listens on.
            </p>
          )}
        </div>
      )}
      {step === 3 && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (changing) {
              save();
            } else {
              next();
            }
          }}
        >
          <h3>Copy back what {providerWord} shows</h3>
          <Field label="Display name" hint="What people see on the sign-in button.">
            <TextInput
              value={d.displayName}
              onChange={(v) => {
                set({ displayName: v });
              }}
              placeholder="Microsoft Entra"
              autoFocus
            />
          </Field>
          {issuerChanged && (
            <Notice kind="info">A new issuer is a new provider; set it up and switch to it.</Notice>
          )}
          <ProviderFields
            d={d}
            clients={clientsOf(d.kind)}
            more
            set={(patch) => {
              set(patch);
            }}
          />
          {needsSecret && (
            <Field
              label="Client secret"
              hint={
                (initial?.client_secret_path ?? "") !== ""
                  ? "One is held; leave empty to keep it. Typed once, never shown."
                  : `Shown by ${providerWord} with the client; typed once, never shown again.`
              }
            >
              <TextInput
                value={d.secret}
                onChange={(v) => {
                  set({ secret: v });
                }}
                type="password"
              />
            </Field>
          )}
        </form>
      )}
      {step === 4 && (
        <div className="stack">
          <h3>Who gets in</h3>
          <p className="muted">
            At least one access rule under {providerWord} that admits an admin, since a switch is refused
            unless the admin switching matches one. More rules can be added later on People.
          </p>
          <Field label="Claim" hint="A group or role claim, or email for one person.">
            <TextInput
              value={d.ruleClaim}
              onChange={(v) => {
                set({ ruleClaim: v });
              }}
              mono
            />
          </Field>
          <Field label="Value" hint={`For example ${preset.ruleExample}.`}>
            <TextInput
              value={d.ruleValue}
              onChange={(v) => {
                set({ ruleValue: v });
              }}
              mono
            />
          </Field>
          <p className="small faint">
            {d.ruleClaim.trim() !== "" && d.ruleValue.trim() !== ""
              ? `${d.ruleClaim.trim()} = ${d.ruleValue.trim()} → admin`
              : "No rule yet: you can add one on People before switching."}
          </p>
        </div>
      )}
      {step === 5 && (
        <p className="muted">{providerWord} is set up. Nobody signs in with it until you switch.</p>
      )}
    </FormPage>
  );
}

/**
 * Switch is the switch, either way: what changes and who carries over, read
 * from the rows before anything happens, then one button that runs the
 * provider's own sign-in as proof.
 */
function Switch({
  target,
  fromId,
  fromSubjectClaim,
  toName,
  own,
  managedConfig,
  onClose,
}: {
  target: Probe;
  fromId: string;
  fromSubjectClaim: string | undefined;
  toName: string;
  own: ProviderConfig | undefined;
  managedConfig: ProviderConfig | undefined;
  onClose: () => void;
}) {
  const s = useSurface();
  const { origin, platform } = usePage();
  const [rows] = useLoad("rows", () => s.bus.call<RowsListReply>(SERVICE, Subjects.rowsList(), undefined));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [open, setOpen] = useState(false);
  const carry = rows.state === "ready" ? carryOf(rows.value.rows, fromId, fromSubjectClaim) : undefined;
  const rules = carry?.notCarried.filter(isRule) ?? [];
  const people = carry?.notCarried.filter((r) => !isRule(r)) ?? [];

  const go = () => {
    setErr(undefined);
    const provider = target === "own" ? own : (managedConfig ?? "platform");
    if (provider === undefined) {
      return;
    }
    const choice = proofChoice(origin, platform, s.tenant, provider);
    if (choice === undefined) {
      setErr(new Error(`${toName} offers no sign-in a browser can use`));
      return;
    }
    setBusy(true);
    begin(origin, choice, tenantPath(s.tenant, "settings"), target).then(
      (to) => {
        window.location.assign(to);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };

  return (
    <FormPage
      title={target === "own" ? `Switch to ${toName}` : `Switch back to ${toName}`}
      heading="h2"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button kind="primary" onClick={go} disabled={busy || rows.state === "pending"}>
            {busy ? "Opening the sign-in…" : `Sign in with ${toName} and switch`}
          </Button>
        </>
      }
    >
      {err !== undefined && <Notice kind="bad">{describe(err)}</Notice>}
      <div className="stack">
        <p>
          Everyone is signed out of {s.name}, and from then on signs in with {toName}.
        </p>
        {rows.state === "pending" && <Pending what="Reading who carries over" />}
        {rows.state === "refused" && <NotAllowed err={rows.err} what="Access rules" />}
        {carry !== undefined && (
          <>
            {carry.carried.length > 0 && (
              <div>
                <button
                  type="button"
                  className="text-link"
                  onClick={() => {
                    setOpen((o) => !o);
                  }}
                >
                  {plural(carry.carried.length, "person keeps", "people keep")} their role, matched by their
                  email
                </button>
                {open && (
                  <ul className="small muted">
                    {carry.carried.map((c) => (
                      <li key={c.email}>
                        {c.email} — {c.role}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {carry.notCarried.length > 0 && (
              <div>
                <p>
                  {[
                    rules.length > 0 ? plural(rules.length, "access rule") : "",
                    people.length > 0 ? plural(people.length, "person", "people") : "",
                  ]
                    .filter((x) => x !== "")
                    .join(" and ")}{" "}
                  {carry.notCarried.length === 1 ? "does" : "do"} not carry over — add them again after the
                  switch:
                </p>
                <ul className="small muted">
                  {carry.notCarried.map((r) => (
                    <li key={`${r.claim}=${r.value}`}>
                      {whoOf(r)} — {r.role}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="small muted">No invite is sent; use Send again on People to tell them.</p>
          </>
        )}
        <p className="small muted">
          Under {toName} each person is new to Impire: apps registered under their old sign-in are suspended
          when their attestation runs out, unless an admin restores them, and secrets they stored stay with
          their old sign-in.
        </p>
        <p className="small faint">
          Your sign-in must match an admin rule under {toName}. Everyone is signed out the moment the switch
          happens, you included.
        </p>
      </div>
    </FormPage>
  );
}
